import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Button } from "react-bootstrap";
import Swal from "sweetalert2";
import { formatDateTime } from "../../utils/formatters";
import PaginatedTable from "../Common/PaginatedTable";
import Unauthorized from "../../components/Common/Unauthorized";
import usePermission from "../../hooks/usePermission";
import {
  getCustomersPaged,
  getCustomerById,
  updateCustomer,
  deleteCustomer,
} from "../../services/customerService";
import { Pencil, Trash2, CheckCircle, XCircle, Users, Eye } from "lucide-react";
import CustomerDetailsModal from "./CustomerDetailsModal.jsx";

const customStyles = {
  headCells: {
    style: {
      justifyContent: "center",
      textAlign: "center",
    },
  },
};

const CustomerPage = () => {
  const navigate = useNavigate();
  const hasPermission = usePermission();

  // tabla
  const [customers, setCustomers] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);

  // modal detalle
  const [showDetails, setShowDetails] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchCustomers = useCallback(async (page, size, search = "") => {
    try {
      setLoading(true);
      const res = await getCustomersPaged(page, size, search);
      if (!res?.success) throw new Error(res?.message);
      setCustomers(res.data.items || []);
      setTotalRows(res.data.totalCount || 0);
    } catch {
      Swal.fire("Error", "No se pudieron cargar los clientes", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomers(currentPage, perPage, searchTerm);
  }, [fetchCustomers, currentPage, perPage, searchTerm]);

  // paginación/busca
  const onPageChange = (p) => setCurrentPage(p);
  const onRowsChange = (n, p) => {
    setPerPage(n);
    setCurrentPage(p);
  };
  const onSearch = (t) => {
    setSearchTerm(t);
    setCurrentPage(1);
  };

  // navegación
  const goCreate = () => navigate("/dashboard/customers/create");
  const goEdit = (id) => navigate(`/dashboard/customers/edit/${id}`);

  // delete (soft delete)
  const onDelete = async (id) => {
    const ok = await Swal.fire({
      title: "¿Eliminar cliente?",
      text: "Esta acción realizará un borrado lógico.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });
    if (!ok.isConfirmed) return;

    try {
      const res = await deleteCustomer(id);
      if (!res?.success) throw new Error();
      Swal.fire("Eliminado", "El cliente fue eliminado.", "success");
      fetchCustomers(currentPage, perPage, searchTerm);
    } catch {
      Swal.fire("Error", "No se pudo eliminar el cliente.", "error");
    }
  };

  // activar/inactivar (soporta clientes SIN SecurityUser)
  const onToggleActive = async (row) => {
    const next = !row.isActive;
    const ask = await Swal.fire({
      title: next ? "¿Activar cliente?" : "¿Inactivar cliente?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, confirmar",
      cancelButtonText: "Cancelar",
    });
    if (!ask.isConfirmed) return;

    try {
      const r = await getCustomerById(row.id);
      if (!r?.success) throw new Error();
      const d = r.data;

      // Payload alineado con CustomerUpdateDto
      const payload = {
        id: d.id,
        // Identidad / tipo
        customerTypeId: d.customerTypeId,
        documentTypeId: d.documentTypeId,
        documentNumber: d.documentNumber,

        // Persona / contacto
        firstName: d.firstName,
        lastName: d.lastName,
        birthDate: d.birthDate,
        genderId: d.genderId,
        phoneMobile: d.phoneMobile,
        phoneAlt: d.phoneAlt,
        email: d.email,

        // Dirección / geo
        addressLine1: d.addressLine1,
        addressLine2: d.addressLine2,
        countryId: d.countryId,
        departmentId: d.departmentId,
        municipalityId: d.municipalityId,

        // Comercial / fiscal
        companyName: d.companyName,
        taxId: d.taxId,
        taxVerificationDigit: d.taxVerificationDigit,
        businessActivity: d.businessActivity,

        // Perfil
        notes: d.notes,

        // Estado
        isActive: next,
      };

      // Solo enviamos SecurityUser si realmente existe (modo extendido)
      if (d.securityUserId) {
        payload.securityUser = {
          id: d.securityUserId,
          firstName: d.firstName,
          lastName: d.lastName,
          email: d.email,
          isActive: next,
          password: undefined,
          confirmPassword: undefined,
          roleIds: Array.isArray(d.roleIds) ? d.roleIds : [],
        };
      }

      const res = await updateCustomer(row.id, payload);
      if (!res?.success) throw new Error();
      Swal.fire("Éxito", "Estado actualizado.", "success");
      fetchCustomers(currentPage, perPage, searchTerm);
    } catch {
      Swal.fire("Error", "No se pudo actualizar el estado.", "error");
    }
  };

  // abrir modal detalle
  const onView = async (id) => {
    setDetailId(id);
    setShowDetails(true);
    setDetail(null);
    setDetailLoading(true);
    try {
      const r = await getCustomerById(id);
      if (!r?.success) throw new Error();
      setDetail(r.data);
    } catch {
      Swal.fire("Error", "No se pudo cargar el detalle.", "error");
      setShowDetails(false);
    } finally {
      setDetailLoading(false);
    }
  };

  // permisos
  if (!hasPermission("clientes.view")) return <Unauthorized />;

  const columns = [
    {
      name: "Nombre",
      selector: (r) => r.displayName || r.fullName || "—",
      sortable: true,
      wrap: true,
      width: "260px",
      cell: (r) => (
        <div className="d-flex flex-column">
          <span className="fw-semibold">
            {r.displayName || r.fullName || "—"}
          </span>
          <small className="text-muted">
            {r.customerTypeName || "Tipo no definido"}
          </small>
        </div>
      ),
    },
    {
      name: "Documento",
      selector: (r) => r.documentNumber,
      sortable: true,
      width: "220px",
      cell: (r) => (
        <div className="d-flex flex-column">
          <span>
            {(r.documentTypeCode || "").toUpperCase()} {r.documentNumber}
          </span>
          {r.email && (
            <small className="text-muted" style={{ fontSize: 11 }}>
              {r.email}
            </small>
          )}
        </div>
      ),
    },
    {
      name: "Celular",
      selector: (r) => r.phoneMobile,
      width: "140px",
    },

    {
      name: "Creado",
      selector: (r) => formatDateTime(r.createdAt),
      sortable: true,
      width: "200px",
    },
    {
      name: "Activo",
      headerCenter: true,
      center: true,
      width: "150px",
      cell: (row) =>
        hasPermission("clientes.update") ? (
          <Button
            variant="light"
            size="sm"
            className="d-flex align-items-center gap-1 border-0"
            onClick={(e) => {
              e.stopPropagation();
              onToggleActive(row);
            }}
          >
            {row.isActive ? (
              <>
                <CheckCircle size={16} className="text-success" /> Activo
              </>
            ) : (
              <>
                <XCircle size={16} className="text-danger" /> Inactivo
              </>
            )}
          </Button>
        ) : row.isActive ? (
          <span className="text-success fw-semibold">Activo</span>
        ) : (
          <span className="text-danger fw-semibold">Inactivo</span>
        ),
    },
    {
      name: "Acciones",
      center: true,
      width: "230px",
      cell: (row) => (
        <div className="d-flex gap-2">
          {hasPermission("clientes.update") && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                goEdit(row.id);
              }}
              title="Editar"
            >
              <Pencil size={16} />
            </Button>
          )}
          {hasPermission("clientes.delete") && (
            <Button
              variant="outline-danger"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(row.id);
              }}
              title="Eliminar"
            >
              <Trash2 size={16} />
            </Button>
          )}
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onView(row.id);
            }}
            title="Ver detalles"
          >
            <Eye size={16} className="me-1" /> Ver datos
          </Button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  return (
    <div className="container-fluid mt-4 px-4 w-100">
      <Card className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-2">
            <Users size={22} />
            <h4 className="mb-0 text-dark">Clientes</h4>
          </div>

          {hasPermission("clientes.create") && (
            <Button onClick={goCreate}>+ Crear cliente</Button>
          )}
        </div>

        <PaginatedTable
          keyField="id"
          columns={columns}
          data={customers}
          totalRows={totalRows}
          perPage={perPage}
          currentPage={currentPage}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsChange}
          onSearch={onSearch}
          progressPending={loading}
          customStyles={customStyles}
        />
      </Card>

      <CustomerDetailsModal
        visible={showDetails}
        onClose={() => setShowDetails(false)}
        data={detail}
        loading={detailLoading}
        onReload={() =>
          getCustomerById(detailId).then((r) => {
            if (r?.success) setDetail(r.data);
          })
        }
      />
    </div>
  );
};

export default CustomerPage;
